import MomentUtils from '@mui/lab/AdapterMoment';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import { Box, Button, InputLabel, TextField, Typography } from '@mui/material';
import { Loader } from 'components';
import ContentMultiple from 'components/ContentMultiple/ContentMultiple';
import InputDate from 'components/InputDate';
import Select, { PropertyNameOptions, SelectTypeOptions } from 'components/Select';
import SelectUser from 'components/SelectUser';
import useDeliverableService from 'features/Deliverables/hooks/useDeliverableService';
import {
  canEveryDeliverableHaveDateChange,
  isEveryDeliverableCustom,
  isEveryDeliverableNotFromQuote,
} from 'features/Deliverables/services/deliverableService';
import useUserRoles from 'hooks/useUserRoles';
import { pickBy } from 'lodash';
import moment, { Moment } from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { closeDrawerAction } from 'store/actions/drawerActions';
import { setSnackbarAction } from 'store/actions/snackbarActions';
import { useSelector } from 'store/hooks';
import { useAppState } from 'store/Provider';
import { selectConsultantsForTnM, selectPurchaseOrderForTnM } from 'store/selectors/deliverableSelectors';
import OrderWorkunit from 'types/entities/OrderWorkunit';
import Scope from 'types/entities/Scope';
import User from 'types/entities/User';
import useApi from 'hooks/useApi';
import OrderApiService from 'api/OrderApiService';
import { getPurchaseOrderAction } from 'store/actions/purchaseOrderActions';
import { formatDateToddMMYYY } from 'helpers/format';

import ChangeBaseLineAPIServices from 'api/ChangeBaseLineAPIServices';
import { closeDialogAction, closeDialogWithDataAction } from 'store/actions/dialogsActions';
import { filterTotalFlagAction, singleWorkunitSaveAction } from 'store/actions/changeBaselineActions';
import { addLoadingAction, removeLoadingAction } from 'store/actions/loadingsActions';
import useBaseLineOrderWorkunitApi from 'features/ChangeBaseline/ChangeBaselinePages/services/useBaseLineOrderWorkunitApi';
import { LoadingButton } from '@mui/lab';
import { injectScopesAction } from 'store/actions/catalogToOrderActions';
// import { SearchParams } from 'api/ResourceAPI';
// import useTotalWorkunitService from '../Hooks/useTotalWorkunitService';

type DeliverableEditProps = {
  deliverables: OrderWorkunit[];
};

const DeliverableEdit: React.FC<DeliverableEditProps> = ({ deliverables }) => {
  const { t } = useTranslation();
  const { getDeliverableByListId, isTnM, fetchDeliverables } = useDeliverableService();
  const { EditTotalBaseLineWorkunit } = useBaseLineOrderWorkunitApi();
  const [updateDateReason, setUpdateDateReason] = useState<string | null>(null);
  const [isChangeDateTextareaOpen, setIsChangeDateTextareaOpen] = useState(false);
  const { state, dispatch } = useAppState();
  const { baseLineOrder, pageSize, currentPage, singleWorkunitSave } = useSelector((state) => state.changeBaseline);
  const customers = useSelector((state) => state?.changeBaseline?.customers);
  const changeBaseline = useSelector((state) => state?.changeBaseline);
  const loadings = useSelector((state) => state.loadings);
  const [dateValid, setDateValid] = useState<boolean>(true);
  const [mission_advan_id, setMission_advan_id] = useState<number | null>(null);
  const { dialogs } = state;
  const { fromPopup, data, type } = dialogs?.data;

  const userRoles = useUserRoles();
  const [scopes] = useState<Scope[]>(baseLineOrder?.tmscopes ?? []);
  const { makeCall } = useApi();
  const purchaseOrder = useSelector(selectPurchaseOrderForTnM);
  // const { handleBLTotalworkunitSearchParams } = useTotalWorkunitService();

  const tmAccountables = baseLineOrder?.tmaccountables;
  const tmConsultants = useSelector(selectConsultantsForTnM);
  const appFiltersSelected = useSelector((state) => state.filter?.selected);
  const consultants = isTnM() ? tmConsultants : baseLineOrder?.tmconsultants;
  const userLocale = navigator.language || 'fr-FR';
  const dateFormat = userLocale !== 'en-US' ? 'DD/MM/YYYY' : 'MM/DD/YYYY';

  const [paramsForEdition, setParamsForEdition] = useState<Partial<OrderWorkunit>>({});
  const [submitFlag, setSubmitFlag] = useState(false);

  const [orderWorkunits, setOrderWorkunits] = useState<OrderWorkunit[]>([]);

  useEffect(() => {
    const getPoScopeByBaseline = async (currentOrder: any) => {
      const { purchase_orders, order_scopes } = await OrderApiService.fetchPoScopeBaseline(
        Number(baseLineOrder?.id),
        2,
        currentOrder?.affair?.otp as string
      );
      if (purchase_orders.length > 0) {
        purchase_orders.forEach(
          (po: { poend: string; poref: string; reference: string; postart: string; is_valid: boolean }) => {
            po.reference = `${po.poref} (${formatDateToddMMYYY(po.postart)} - ${formatDateToddMMYYY(po.poend)})`;
          }
        );
      }

      dispatch(injectScopesAction(order_scopes?.datas));
      dispatch(getPurchaseOrderAction(purchase_orders));
    };

    getPoScopeByBaseline(baseLineOrder);
  }, []);

  const getSelectedDeliverableByListId = useCallback(async () => {
    // const deliverables = await getDeliverableByListId(baseLineOrder?.id as number, ids);
    const filteredArray: User[] =
      changeBaseline?.deliveryManagers?.filter(
        (user) =>
          `${user.last_name.toLocaleLowerCase()} ${user.first_name.toLocaleLowerCase()}` ===
          deliverables[0]?.delivery_manager_fullname?.toLocaleLowerCase()
      ) || [];

    if (deliverables?.length === 1 && !fromPopup) {
      setParamsForEdition(() => ({
        ...paramsForEdition,
        client: deliverables[0]?.client,
        scope: deliverables[0]?.scope,
        consultant: deliverables[0]?.consultant,
        delivery_manager: filteredArray,
        content: deliverables[0]?.content,
        content2: deliverables[0]?.content2,
        content3: deliverables[0]?.content3,
        content4: deliverables[0]?.content4,
        content5: deliverables[0]?.content5,
        purchase_order_id: deliverables[0]?.purchase_order_id,
        purchase_order: deliverables[0]?.purchase_order,
      }));

      setMission_advan_id(Number(deliverables[0]?.mission_advancement_id));
    }

    setOrderWorkunits(() => deliverables);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getDeliverableByListId, deliverables, baseLineOrder?.id]);

  useEffect(() => {
    if (!submitFlag) getSelectedDeliverableByListId();
  }, [getSelectedDeliverableByListId, deliverables, baseLineOrder?.id]);

  useEffect(() => {
    if (singleWorkunitSave) {
      submit();
      dispatch(singleWorkunitSaveAction(false));
    }
  }, [singleWorkunitSave]);

  const submit = async (e?: { preventDefault: () => void }) => {
    e?.preventDefault();
    setSubmitFlag(true);
    if (orderWorkunits)
      try {
        const updateWithReason: OrderWorkunit['id'][] = [];
        const updateWithoutReason: OrderWorkunit['id'][] = [];
        dispatch(addLoadingAction('EditBLDeliveriable'));
        let cleanedObject = pickBy(paramsForEdition, (v) => v !== null);
        cleanedObject = Object.entries(cleanedObject).reduce((acc, [key, value]) => {
          if (value === 0 && key.endsWith('_id')) {
            const name = key.replace('_id', '');
            const fullNameKey = `${name}_fullname`;
            acc = {
              ...acc,
              [name]: null,
              [fullNameKey]: null,
              [key]: null,
            };
          } else {
            acc = {
              ...acc,
              [key]: value,
            };
          }
          return acc;
        }, {});
        if (paramsForEdition.consultant_id) {
          cleanedObject.consultant_id = paramsForEdition.consultant_id;
        }

        const clientData = {
          client: cleanedObject.client,
          client_fullname: `${cleanedObject.client?.first_name} ${cleanedObject.client?.last_name}`,
          client_id: cleanedObject.client?.id,
        };

        const consultantData = {
          consultant: cleanedObject.consultant,
          consultant_fullname: `${cleanedObject.consultant?.first_name} ${cleanedObject.consultant?.last_name}`,
          consultant_id: cleanedObject.consultant?.id,
        };

        const deliveryManagerData = {
          delivery_manager_id:
            cleanedObject?.delivery_manager && Array.isArray(cleanedObject.delivery_manager)
              ? cleanedObject.delivery_manager[0]?.id ?? null
              : null,
          delivery_manager: cleanedObject.delivery_manager,
          delivery_manager_fullname:
            cleanedObject?.delivery_manager && Array.isArray(cleanedObject.delivery_manager)
              ? `${cleanedObject?.delivery_manager[0]?.last_name} ${cleanedObject?.delivery_manager[0]?.first_name}`
              : null,
        };

        const scopeData = {
          scope: cleanedObject.scope,
          scope_id: cleanedObject.scope?.id, // Optional chaining for scope.id
        };

        const customData = {
          workunit_desc: cleanedObject?.workunit_desc ?? deliverables[0]?.workunit_desc,
          workunit_name: cleanedObject?.workunit_name ?? deliverables[0]?.workunit_name,
          workunit_reference: cleanedObject?.workunit_reference ?? deliverables[0]?.workunit_reference,
        };

        const savedDeliverables = {
          ...deliverables[0],
          ...clientData,
          ...consultantData,
          ...deliveryManagerData,
          ...scopeData,
          ...customData,
          modification_reason: paramsForEdition?.modification_reason ?? null,
          forecast_date: cleanedObject?.forecast_date,
          delivery_date: cleanedObject?.delivery_date,
          content: cleanedObject.content,
          content2: cleanedObject.content2,
          content3: cleanedObject.content3,
          content4: cleanedObject.content4,
          content5: cleanedObject.content5,
          purchase_order: cleanedObject.purchase_order,
          purchase_order_id: cleanedObject.purchase_order_id,
          workunit_type: 'single',
          source_order_id: changeBaseline?.sourceOrder?.id,
          page_typeid: 3,
          status: 1,
        };

        const res = await EditTotalBaseLineWorkunit(
          Number(changeBaseline?.baseLineOrder?.id),
          savedDeliverables,
          Number(changeBaseline?.sourceOrder?.id)
        );

        // const res = await ChangeBaseLineAPIServices.updateBaseLineWorkunit(baseLineOrder?.id, {
        //   ...savedDeliverables,
        //   order_id: Number(changeBaseline?.sourceOrder?.id),
        // });
        if (res) {
          dispatch(filterTotalFlagAction(true));
          setSubmitFlag(false);
          if (fromPopup) {
            dispatch(closeDialogWithDataAction({ name: 'BaselineEditWorkunitCancel', data: { data, type } }));
            dispatch(closeDialogWithDataAction({ name: 'changeBaselineeditworkunitDialog', data: { data, type } }));
          } else {
            dispatch(closeDialogAction('changeBaselineeditworkunitDialog'));
          }

          // dispatch(closeDialogWithDataAction({ name: 'changeBaselineeditworkunitDialog', data: dialogs?.data }));
          dispatch(setSnackbarAction({ message: 'Saved', open: true, severity: 'success', duration: 500 }));
          dispatch(removeLoadingAction('EditBLDeliveriable'));
        }
      } catch (error) {
        setSubmitFlag(false);
        dispatch(
          setSnackbarAction({
            message: 'Error while updating workunit',
            open: true,
            severity: 'error',
          })
        );
        dispatch(removeLoadingAction('EditBLDeliveriable'));
      } finally {
        setSubmitFlag(false);
        dispatch(removeLoadingAction('EditBLDeliveriable'));
        // fetchDeliverables(baseLineOrder?.id, { current: null });
        // dispatch(closeDrawerAction());
      }
  };

  const handleDateChange = (date: Moment | null, name: string) => {
    if (date) {
      if (!date.isValid()) setDateValid(false);
      if (date.isValid()) setDateValid(true);
      setParamsForEdition({
        ...paramsForEdition,
        [name]: moment(date).format('YYYY-MM-DD'),
      });
    } else {
      setDateValid(true);
      setParamsForEdition({
        ...paramsForEdition,
        [name]: null,
      });
    }
  };

  const everyDeliverableIsNotFromQuote = isEveryDeliverableNotFromQuote(orderWorkunits);
  //  || state.selectedAllDeliverables;
  // const everyDeliverableIsCustom = isEveryDeliverableCustom(orderWorkunits) || state.selectedAllDeliverables;
  const everyDeliverableIsCustom = isEveryDeliverableCustom(orderWorkunits);
  useEffect(() => {
    const forecastDate: string | undefined = paramsForEdition.forecast_date as string | undefined;

    const isForecastDateChanged = (): boolean =>
      forecastDate !== undefined && orderWorkunits && orderWorkunits[0].forecast_date !== forecastDate;
    let isChangeDateTextareaOpen = false;

    if (everyDeliverableIsNotFromQuote) {
      isChangeDateTextareaOpen = false;
    } else if (orderWorkunits.length === 1 && orderWorkunits) {
      isChangeDateTextareaOpen = isForecastDateChanged();
    } else if (orderWorkunits) {
      isChangeDateTextareaOpen = isForecastDateChanged();
    } else if (forecastDate !== undefined) {
      isChangeDateTextareaOpen = true;
    }

    setIsChangeDateTextareaOpen(() => isChangeDateTextareaOpen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [everyDeliverableIsNotFromQuote, orderWorkunits, paramsForEdition]);

  const getDefaultDisplayedValue = useCallback(
    (
      field: keyof Pick<
        OrderWorkunit,
        | 'workunit_name'
        | 'workunit_reference'
        | 'content'
        | 'delivery_date'
        | 'forecast_date'
        | 'content2'
        | 'workunit_desc'
        | 'purchase_order'
      >
    ): string => {
      if (paramsForEdition[field] !== undefined) {
        return paramsForEdition[field] ?? ''; // Explicitly handle null
      }

      if (orderWorkunits.length === 1 && !paramsForEdition[field] && orderWorkunits[0][field])
        return orderWorkunits[0][field] ?? '';

      // if (paramsForEdition[field]) return paramsForEdition[field] ?? '';
      return '';
    },
    [orderWorkunits, paramsForEdition]
  );

  const handleSelectOptions = (e: SelectTypeOptions | null, property: PropertyNameOptions) => {
    // if (e && property === 'scope') {
    //   const filteredDeliveryManager = baseLineOrder?.tmaccountables || [];
    //   // const assignment = scopes_inorder.find((a) => a.id === e.id);
    //   // if (!assignment) {
    //   //   setParamsForEdition({
    //   //     ...paramsForEdition,
    //   //     scope: e as Scope,
    //   //     scope_id: e.id,
    //   //     delivery_manager: [],
    //   //     delivery_manager_ids: [],
    //   //   });
    //   //   return;
    //   // }
    //   // const userIndex = filteredDeliveryManager.findIndex((u) => u.id === assignment.accountable_id);
    //   // if (userIndex === -1) {
    //   //   setParamsForEdition({
    //   //     ...paramsForEdition,
    //   //     scope: e as Scope,
    //   //     scope_id: e.id,
    //   //     delivery_manager: [],
    //   //     delivery_manager_ids: [],
    //   //   });
    //   //   return;
    //   // }
    //   const reorderedUsers = [...filteredDeliveryManager];
    //   const [selectedUser] = reorderedUsers.splice(-1, 1);
    //   // const [selectedUser] = reorderedUsers.splice(userIndex, 1);
    //   reorderedUsers.unshift(selectedUser);
    //   setParamsForEdition({
    //     ...paramsForEdition,
    //     scope: e as Scope,
    //     scope_id: e.id,
    //     delivery_manager: [reorderedUsers[0]],
    //     delivery_manager_ids: [reorderedUsers[0].id],
    //   });
    // }
    if (e && e !== null && property === 'purchase_order') {
      const selectPurchaseOrder = purchaseOrder.find((po) => po.id === e.id);
      if (selectPurchaseOrder) {
        setParamsForEdition({
          ...paramsForEdition,
          purchase_order_id: selectPurchaseOrder?.id, // Just set the id (number)
          purchase_order: selectPurchaseOrder?.poref,
        });
      }
    }
  };
  const getDefaultScope = useCallback(() => {
    if (orderWorkunits.length === 1) return orderWorkunits[0].scope;
    return undefined;
  }, [orderWorkunits]);

  const [autorization, setAutorization] = useState({
    everyDeliverableIsUnsubmitted: false,
    everyDeliverableCanHaveDateChange: false,
  });

  const everyDeliverableIsUnsubmitted = useCallback(
    () => isEveryDeliverableUnsubmitted(orderWorkunits),
    [orderWorkunits]
  );
  const everyDeliverableCanHaveDateChange = useCallback(
    () => canEveryDeliverableHaveDateChange(orderWorkunits),
    [orderWorkunits]
  );

  const isDeliverableCancelled = (deliverable: OrderWorkunit) => deliverable?.label?.includes('cancelled');
  const isEveryDeliverableUnsubmitted = (deliverables: OrderWorkunit[]) =>
    deliverables?.every(
      (deliverable) =>
        (!deliverable.mission_advancement_id || deliverable.mission_advancement_id === 2) &&
        !isDeliverableCancelled(deliverable)
    );

  useEffect(() => {
    setAutorization(() => ({
      everyDeliverableIsUnsubmitted: everyDeliverableIsUnsubmitted(),
      everyDeliverableCanHaveDateChange: everyDeliverableCanHaveDateChange(),
    }));
  }, [everyDeliverableCanHaveDateChange, everyDeliverableIsUnsubmitted]);

  const flag_disable = mission_advan_id !== null && mission_advan_id > 0;
  return (
    <form onSubmit={submit} className="deliverables-edit-container">
      {orderWorkunits?.length === 1 && (
        <Box sx={{ backgroundColor: 'primary.light', borderRadius: '10px', p: 3, mb: 3 }}>
          {/* <Box sx={{ mb: 1 }}>
            <Typography>{t('Scope_name')}</Typography>
            <Typography
              sx={{
                color: 'primary.main',
                fontWeight: 500,
              }}
            >
              {orderWorkunits[0]?.scope?.name ? orderWorkunits[0]?.scope?.name : t('No_scope')}
            </Typography>
          </Box> */}
          {orderWorkunits[0].workunit_reference && (
            <Box sx={{ mb: 1 }}>
              <Typography>{t('REFERENCE_OF_WORKUNIT')}</Typography>
              <Typography
                sx={{
                  color: 'primary.main',
                  fontWeight: 500,
                }}
              >
                {orderWorkunits[0].workunit_reference}
              </Typography>
            </Box>
          )}
          <Box>
            <Typography>{t('Initial_Deliverable_date')}</Typography>
            <Typography
              sx={{
                color: 'primary.main',
                fontWeight: 500,
              }}
            >
              {orderWorkunits[0].delivery_date ||
                // moment(orderWorkunits[0].forecast_date).format('DD-MM-YYYY') ||
                t('Not_defined')}
            </Typography>
          </Box>
          {orderWorkunits[0].workunit_desc && (
            <Box sx={{ mb: 1 }}>
              <Typography>{t('DESCRIPTION_NEW_WORKUNIT')}</Typography>
              <Typography
                sx={{
                  color: 'primary.main',
                  fontWeight: 500,
                }}
              >
                {orderWorkunits[0].workunit_desc}
              </Typography>
            </Box>
          )}
        </Box>
      )}
      {loadings.api ? (
        <Loader />
      ) : (
        <>
          {everyDeliverableIsCustom && (
            <>
              <Box
                sx={{
                  mb: 3,
                  opacity: flag_disable ? 0.3 : 1,
                  pointerEvents: flag_disable ? 'none' : 'auto',
                }}
              >
                <InputLabel htmlFor="mission_name">{t('Name')}</InputLabel>
                <TextField
                  name="workunit_name"
                  id="mission_name"
                  className="deliverables-input active-blue"
                  placeholder={t('Name_deliverable')}
                  onChange={(e) => setParamsForEdition({ ...paramsForEdition, [e.target.name]: e.target.value })}
                  value={paramsForEdition.workunit_name ?? getDefaultDisplayedValue('workunit_name')}
                  required
                  fullWidth
                  disabled={mission_advan_id !== null && mission_advan_id > 0}
                />
              </Box>
              <Box
                sx={{
                  mb: 3,
                  opacity: flag_disable ? 0.3 : 1,
                  pointerEvents: flag_disable ? 'none' : 'auto',
                }}
              >
                <InputLabel htmlFor="mission_ref">{t('Reference')}</InputLabel>
                <TextField
                  name="workunit_reference"
                  id="mission_ref"
                  className="deliverables-input active-blue"
                  placeholder={t('Deliverable_Reference')}
                  onChange={(e) => setParamsForEdition({ ...paramsForEdition, [e.target.name]: e.target.value })}
                  required
                  value={paramsForEdition.workunit_reference ?? getDefaultDisplayedValue('workunit_reference') ?? ''}
                  fullWidth
                  disabled={mission_advan_id !== null && mission_advan_id > 0}
                />
              </Box>
              <Box
                sx={{
                  mb: 3,
                  opacity: flag_disable ? 0.3 : 1,
                  pointerEvents: flag_disable ? 'none' : 'auto',
                }}
              >
                <InputLabel htmlFor="workunit_desc">{t('Description')}</InputLabel>
                <TextField
                  name="workunit_desc"
                  id="workunit_desc"
                  rows={4}
                  className="active-blue background-grey no-border deliverables-input "
                  onChange={(e) => setParamsForEdition({ ...paramsForEdition, [e.target.name]: e.target.value })}
                  required
                  placeholder={t('Deliverable_Description')}
                  value={paramsForEdition.workunit_desc ?? getDefaultDisplayedValue('workunit_desc') ?? ''}
                  fullWidth
                  multiline
                  disabled={mission_advan_id !== null && mission_advan_id > 0}
                />
              </Box>
            </>
          )}
          <Box
            sx={{
              mb: 3,
              opacity: flag_disable ? 0.5 : 1,
              pointerEvents: flag_disable ? 'none' : 'auto',
            }}
          >
            <InputLabel htmlFor="content">{t('Content')}</InputLabel>
            <TextField
              name="content"
              id="content"
              rows={4}
              className="active-blue no-border deliverables-input"
              value={paramsForEdition.content ?? getDefaultDisplayedValue('content') ?? ''}
              required
              onChange={(e) =>
                setParamsForEdition({ ...paramsForEdition, [e.target.name]: e.target.value ? e.target.value : ' ' })
              }
              placeholder={t('Deliverable_Content')}
              multiline
              fullWidth
              disabled={mission_advan_id !== null && mission_advan_id > 0}
            />
          </Box>

          {paramsForEdition && mission_advan_id === null && mission_advan_id === 0 && (
            <ContentMultiple orderWorkunit={paramsForEdition as OrderWorkunit} onchange={setParamsForEdition} />
          )}

          {everyDeliverableIsCustom && (
            <LocalizationProvider dateAdapter={MomentUtils}>
              <Box
                sx={{
                  mb: 3,
                  opacity: flag_disable ? 0.5 : 1,
                  pointerEvents: flag_disable ? 'none' : 'auto',
                }}
              >
                <InputDate
                  label={t('Initial_Deliverable_date')}
                  name="delivery_date"
                  id="delivery_date"
                  placeholder={dateFormat}
                  onchange={(name, date) => handleDateChange(date, name)}
                  value={
                    paramsForEdition.delivery_date ??
                    moment(getDefaultDisplayedValue('delivery_date')).format('YYYY-MM-DD')
                  }
                  min={baseLineOrder?.start_date ? moment(baseLineOrder.start_date).format('YYYY-MM-DD') : undefined}
                  max={baseLineOrder?.end_date ? moment(baseLineOrder.end_date).format('YYYY-MM-DD') : undefined}
                  disabled={mission_advan_id !== null && mission_advan_id > 0}
                />
              </Box>
            </LocalizationProvider>
          )}
          <LocalizationProvider dateAdapter={MomentUtils}>
            <Box
              sx={{
                mb: 3,
                opacity: flag_disable ? 0.5 : 1,
                pointerEvents: flag_disable ? 'none' : 'auto',
              }}
            >
              <InputDate
                label={t('New_expected_date')}
                name="forecast_date"
                id="forecast_date"
                placeholder={dateFormat}
                showError
                canEmpty
                // disabled={!everyDeliverableCanHaveDateChange()}
                onchange={(name, date) => handleDateChange(date, name)}
                value={
                  paramsForEdition.forecast_date ??
                  moment(getDefaultDisplayedValue('forecast_date')).format('YYYY-MM-DD')
                }
                // min={moment(state.order?.start_date).toString()}
                // max={moment(state.order?.end_date).add('1', 'year').toString()}
                min={baseLineOrder?.start_date ? moment(baseLineOrder.start_date).format('YYYY-MM-DD') : undefined}
                max={baseLineOrder?.end_date ? moment(baseLineOrder.end_date).format('YYYY-MM-DD') : undefined}
                disabled={mission_advan_id !== null && mission_advan_id > 0}
              />
            </Box>
            {isChangeDateTextareaOpen && (
              <Box
                sx={{
                  mb: 3,
                  opacity: flag_disable ? 0.5 : 1,
                  pointerEvents: flag_disable ? 'none' : 'auto',
                }}
              >
                <InputLabel htmlFor="cancel-reason-input">{t('Comment your modifications')}</InputLabel>
                <TextField
                  name="cancel-reason"
                  id="cancel-reason-input"
                  rows={4}
                  value={updateDateReason ?? ''}
                  required
                  onChange={(e) => {
                    setUpdateDateReason(e.target.value);
                    setParamsForEdition(() => ({
                      ...paramsForEdition,
                      modification_reason: e.target.value,
                    }));
                  }}
                  placeholder={t('Enter your text here')}
                  multiline
                  fullWidth
                  disabled={mission_advan_id !== null && mission_advan_id > 0}
                />
              </Box>
            )}
          </LocalizationProvider>

          <Box sx={{ mb: 3 }}>
            <Select
              label={t('purchase_order')}
              options={purchaseOrder}
              optionsLabels="reference"
              disabled={loadings.api > 0}
              required={false}
              handleSelectOptions={handleSelectOptions}
              selectedOption={purchaseOrder.find((po) => po?.id === paramsForEdition?.purchase_order_id) || null}
              propertyName="purchase_order"
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            {scopes && (
              <Select
                options={scopes}
                selectedOption={paramsForEdition?.scope ?? getDefaultScope() ?? undefined}
                optionsLabels="name"
                label={t('Scopes')}
                handleSelectOptions={(opt) =>
                  setParamsForEdition({
                    ...paramsForEdition,
                    scope_id: opt?.id,
                    scope: opt as Scope | undefined,
                  })
                }
                propertyName="scope"
                required={false}
                // disabled={!(everyDeliverableIsNotFromQuote && autorization.everyDeliverableIsUnsubmitted)}
                disabled={!autorization.everyDeliverableIsUnsubmitted}
              />
            )}
          </Box>
          <Box sx={{ mb: 3 }}>
            {!userRoles.isConsultant && (
              <>
                {!consultants ? (
                  <Loader size={20} />
                ) : (
                  <>
                    <SelectUser
                      users={consultants}
                      selectedUser={paramsForEdition?.consultant ?? null}
                      onSelectUser={(value) => {
                        if (value) {
                          setParamsForEdition({
                            ...paramsForEdition,
                            consultant_id: value?.id,
                            consultant: value as User | undefined,
                          });
                        }
                        // else
                        //   setParamsForEdition({
                        //     ...paramsForEdition,
                        //     consultant_id: 0,
                        //     consultant: null,
                        //   });
                      }}
                      label={t('Consultant')}
                      required={deliverables?.length === 1}
                      // disabled={!autorization.everyDeliverableIsUnsubmitted}
                      disabled={mission_advan_id !== null && mission_advan_id > 0}
                    />
                    {/* {userRoles.isClient ? null : (
                      <>
                        {!autorization.everyDeliverableIsUnsubmitted &&
                          t('To change the consultant, cancel the submission')}
                      </>
                    )} */}
                  </>
                )}
              </>
            )}
          </Box>
          <Box sx={{ mb: 3 }}>
            {!tmAccountables ? (
              <Loader size={20} />
            ) : (
              <>
                <SelectUser
                  users={tmAccountables}
                  selectedUser={
                    paramsForEdition?.delivery_manager?.length ? paramsForEdition?.delivery_manager[0] : null
                  }
                  onSelectUser={(value) => {
                    if (value)
                      setParamsForEdition({
                        ...paramsForEdition,
                        delivery_manager_ids: [value?.id],
                        delivery_manager: [value],
                      });
                    // else
                    //   setParamsForEdition({
                    //     ...paramsForEdition,
                    //     delivery_manager_ids: [],
                    //     delivery_manager: [],
                    //   });
                  }}
                  label={t('Delivery_manager')}
                  required={deliverables?.length === 1}
                  // disabled={userRoles.isClient || !autorization.everyDeliverableIsUnsubmitted}
                  disabled={mission_advan_id !== null && mission_advan_id > 0}
                />
                {/* {userRoles.isClient ? null : (
                  <>
                    {!autorization.everyDeliverableIsUnsubmitted &&
                      t('To change the consultant, cancel the submission')}
                  </>
                )} */}
              </>
            )}
          </Box>
          <Box sx={{ mb: 3 }}>
            {!customers ? (
              <Loader size={20} />
            ) : (
              <>
                <SelectUser
                  users={customers}
                  selectedUser={paramsForEdition?.client ?? null}
                  onSelectUser={(value) => {
                    if (value)
                      setParamsForEdition({
                        ...paramsForEdition,
                        client_id: value?.id,
                        client: value as User | undefined,
                      });
                    // else
                    //   setParamsForEdition({
                    //     ...paramsForEdition,
                    //     client_id: 0,
                    //     client: null,
                    //   });
                  }}
                  label={t('Client')}
                  required={deliverables?.length === 1}
                  // disabled={userRoles.isClient || !autorization.everyDeliverableIsUnsubmitted}
                  disabled={mission_advan_id !== null && mission_advan_id > 0}
                />
                {userRoles.isClient ? null : (
                  <>
                    {!autorization.everyDeliverableIsUnsubmitted &&
                      t('To change the consultant, cancel the submission')}
                  </>
                )}
              </>
            )}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <LoadingButton
              variant="contained"
              onClick={submit}
              loading={!!loadings?.EditBLDeliveriable}
              color="success"
              disabled={
                // (isChangeDateTextareaOpen && !updateDateReason) ||
                !dateValid ||
                !paramsForEdition?.consultant ||
                !paramsForEdition?.delivery_manager?.length ||
                !paramsForEdition?.client ||
                !!loadings.api
              }
            >
              {t('Save')}
            </LoadingButton>
            {/* <Button
              onClick={submit}
              disabled={
                // (isChangeDateTextareaOpen && !updateDateReason) ||
                !dateValid ||
                // !paramsForEdition?.consultant ||
                // !paramsForEdition?.delivery_manager?.length ||
                // !paramsForEdition?.client ||
                !!loadings.api
              }
              // startIcon={<Add />}
              variant="contained"
              color="success"
            >
              {t('Save')}
            </Button> */}
          </Box>
        </>
      )}
    </form>
  );
};

export default DeliverableEdit;
